import React, { useEffect, useState } from "react";
import {
  Stack,
  Button,
  Title,
  Flex,
  Divider,
  Text,
  Container,
} from "@mantine/core";
import {
  fetchSurveyAnswers,
  fetchSurveyQuestions,
  sendSurveyAnswers,
} from "../../api/profileEditingApi";
import {
  UserSurveyAnswersResponse,
  UserSurveyQuestionsResponse,
  SurveyAnswer,
} from "../../types/profile-editing";
import { QuestionBlock } from "./goals-and-preferences/QuestionBlock";
import useProfileStore from "../../store/useProfileStore";
import { useTranslation } from "react-i18next";
import { EditIconSmall } from "../../shared/svg/EditIconSmall";
import { notifications } from "@mantine/notifications";
import { sendAnswers } from "../../api/userApi";

interface Props {
  setChanges: (t: boolean) => void;
}

const removeErrorBorders = () => {
  const elements = document.querySelectorAll(".error-border");
  elements.forEach((el) => el.classList.remove("error-border"));
};

const GoalsAndPreferencesWidget: React.FC<Props> = ({ setChanges }) => {
  const [questions, setQuestions] = useState<
    UserSurveyQuestionsResponse["data"] | null
  >(null);
  const [initialAnswers, setInitialAnswers] = useState<
    UserSurveyAnswersResponse["data"] | null
  >(null);
  const [allAnswers, setAllAnswers] = useState<{
    [key: string]: { answer_ids: number[]; text_answer?: string };
  }>({});
  const [loading, setLoading] = useState(true);
  const [selectedMainOption, setSelectedMainOption] = useState<
    "Yes" | "No" | null
  >(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { profileLanguage, profile } = useProfileStore();
  const { t } = useTranslation();

  useEffect(() => {
    const loadData = async () => {
      try {
        const langKey = Object.keys(profileLanguage!)[0] || "";
        const [questionsRes, answersRes] = await Promise.all([
          fetchSurveyQuestions(langKey),
          fetchSurveyAnswers(langKey),
        ]);

        setQuestions(questionsRes.data);
        setInitialAnswers(answersRes.data);

        const q1 = answersRes.data.goals.find((item) => item.q1)?.q1;
        if (q1 && q1.answers.length > 0 && questionsRes.data?.main_question) {
          const selectedId = q1.answers[0].id;
          const selectedText = questionsRes.data.main_question.options
            .find((opt) => opt.id === selectedId)
            ?.text.toLowerCase();

          if (["yes", "так"].includes(selectedText!)) {
            setSelectedMainOption("Yes");
          } else if (["no", "ні"].includes(selectedText!)) {
            setSelectedMainOption("No");
          }
        }

        const transformAnswers = (
          input: Record<string, SurveyAnswer>[]
        ): {
          [key: string]: { answer_ids: number[]; text_answer?: string };
        } => {
          const result: {
            [key: string]: { answer_ids: number[]; text_answer?: string };
          } = {};
          input.forEach((entry) => {
            const key = Object.keys(entry)[0];
            const value = entry[key];
            result[key] = {
              answer_ids: value.answers.map((a) => a.id),
              ...(value.text_answer ? { text_answer: value.text_answer } : {}),
            };
          });
          return result;
        };

        const formatted = {
          ...transformAnswers(answersRes.data.goals),
          ...transformAnswers(answersRes.data.preferences),
        };

        setAllAnswers(formatted);
      } catch (e) {
        console.error("Failed to load survey:", e);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [profileLanguage]);

  const handleSave = async () => {
    removeErrorBorders();
    const unansweredQuestions: string[] = [];

    const relevantQuestions = [
      questions?.main_question,
      ...(selectedMainOption === "Yes"
        ? questions?.for_yes || []
        : questions?.for_no || []),
    ];

    relevantQuestions
      .filter((question) => question !== undefined)
      .forEach((question) => {
        if (question) {
          const answer = allAnswers[question.key];
          const isOtherSelected = answer?.answer_ids.some((id) =>
            question.options.find(
              (opt) => opt.id === id && opt.text === t("other")
            )
          );

          const isTextMissing = isOtherSelected && !answer?.text_answer?.trim();

          if (!answer || answer.answer_ids.length === 0 || isTextMissing) {
            unansweredQuestions.push(question.key);
          }
        }
      });

    if (unansweredQuestions.length > 0) {
      unansweredQuestions.forEach((key) => {
        const element = document.getElementById(key);
        if (element) {
          element.classList.add("error-border");
        }
      });
      setErrorMessage(t("missing_answers"));
      return;
    }

    try {
      let res: UserSurveyAnswersResponse;

      if (profile?.goals?.length === 0) {
        await sendAnswers(allAnswers);
        res = await fetchSurveyAnswers();
      } else {
        res = await sendSurveyAnswers(allAnswers);
      }

      setInitialAnswers(res.data);

      setChanges(true);
      notifications.show({
        title: t("success"),
        message: t("changed_successfully"),
        icon: <EditIconSmall />,
        color: "#CEB7FF",
        style: {
          border: "1px solid #d3d3d3",
          borderRadius: "8px",
        },
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleAnswerChange = (
    key: string,
    answerIds: number[],
    textAnswer?: string
  ) => {
    if (errorMessage) setErrorMessage(null);
    removeErrorBorders();
    const newAnswers = {
      ...allAnswers,
      [key]: {
        answer_ids: answerIds,
        ...(textAnswer ? { text_answer: textAnswer } : {}),
      },
    };

    if (key === "q1") {
      const selectedText = questions?.main_question.options
        .find((o) => o.id === answerIds[0])
        ?.text?.toLowerCase();

      let newMainOption: "Yes" | "No" | null = null;

      if (selectedText === "yes" || selectedText === "так") {
        newMainOption = "Yes";
      } else if (selectedText === "no" || selectedText === "ні") {
        newMainOption = "No";
      }

      setSelectedMainOption(newMainOption);

      if (newMainOption && questions) {
        const relevantQuestions = [
          questions.main_question,
          ...(newMainOption === "Yes" ? questions.for_yes : questions.for_no),
        ];

        const allowedKeys = relevantQuestions.map((q) => q.key);

        const filteredAnswers = Object.entries(newAnswers)
          .filter(([k]) => allowedKeys.includes(k))
          .reduce((acc, [k, v]) => {
            acc[k] = v;
            return acc;
          }, {} as typeof allAnswers);

        setAllAnswers(filteredAnswers);
        return;
      }
    }

    setAllAnswers(newAnswers);
  };

  if (loading || !questions || !initialAnswers)
    return (
      <Container size="xs" style={{ textAlign: "center", paddingTop: 80 }}>
        <Text>{t("loading")}</Text>
      </Container>
    );

  return (
    <Stack gap="lg">
      <Title size={36}>{t("goals")}</Title>

      <QuestionBlock
        question={questions.main_question}
        selectedAnswers={allAnswers}
        onChange={handleAnswerChange}
      />
      {(selectedMainOption === "Yes" ? questions.for_yes : questions.for_no)
        .filter((q) => q.selection_type === "single")
        .map((q) => (
          <QuestionBlock
            key={q.id}
            question={q}
            selectedAnswers={allAnswers}
            onChange={handleAnswerChange}
          />
        ))}
      <Divider my={40} color="gray.4" h={2} />

      <Title size={36}>{t("preferences")}</Title>
      {(selectedMainOption === "Yes" ? questions.for_yes : questions.for_no)
        .filter((q) => q.selection_type === "multiple")
        .map((q) => (
          <QuestionBlock
            key={q.id}
            question={q}
            selectedAnswers={allAnswers}
            onChange={handleAnswerChange}
          />
        ))}
      {errorMessage && (
        <Text color="#B00020" style={{ textAlign: "end" }} mt={4}>
          {errorMessage}
        </Text>
      )}
      <Flex align="center" gap={20} justify="end">
        <Button onClick={handleSave}>{t("save")}</Button>
      </Flex>
    </Stack>
  );
};

export default GoalsAndPreferencesWidget;
